import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { AnalyticService, NotificationService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Notification } from '@models/common/notification.model';
import { skip } from 'rxjs/operators';

@Component({
    selector: 'app-notification-viewer',
    templateUrl: './notification-viewer.template.html',
    styleUrls: ['./notification-viewer.style.scss'],
    animations: [
        trigger('slideUp', [transition(':leave', [animate('10ms ease-in-out', style({ height: 0 }))])]),
        trigger('fadeIn', [
            state('void', style({ opacity: 0 })),
            transition(':enter', [animate('.35s 700ms', style({ opacity: 1 }))]),
        ]),
    ],
})
export class NotificationViewerComponent implements OnInit {
    @ViewChild('container', { static: true }) container: ElementRef;
    @Input() showArchived = false;
    @Output() notificationsUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() readStateUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
    isLoading = true;
    notifications: Notification[] = [];

    activeNotification: Notification = null;

    isViewNotificationModalOpen = false;

    hasTabs = true;

    hasImprovedHomepageFlag = false;

    constructor(
        public notificationService: NotificationService,
        public analyticService: AnalyticService,
        public featureService: FeatureService,
        private notify: NotifyService,
        private router: Router
    ) {
        // We skip the first value since it will be an empty array and will trigger unnecessary calls to the backend
        this.notificationService.notifications$.pipe(takeUntilDestroyed(), skip(1)).subscribe((notifications) => {
            this.notifications = notifications;
            this.notificationsUpdated.emit();
        });
    }

    async ngOnInit(): Promise<void> {
        this.hasImprovedHomepageFlag = await this.featureService.has(FeatureFlag.improvedHomepage);

        if (!this.hasImprovedHomepageFlag) {
            this.reloadCurrentNotifications();
        }
    }

    onToggleRead(n: Notification): void {
        let pendoEvent = null;
        if (n?.archivedAt) {
            pendoEvent = n.isReadStatus
                ? AnalyticEvents.NotificationsMarkArchivedAsUnread
                : AnalyticEvents.NotificationsMarkArchivedAsRead;
        } else {
            pendoEvent = n.isReadStatus
                ? AnalyticEvents.NotificationsMarkAsUnread
                : AnalyticEvents.NotificationsMarkAsRead;
        }

        this.analyticService.trackEvent(pendoEvent);

        this.notificationService
            .toggleRead(n)
            .then(() => this.readStateUpdated.emit())
            .catch(() => this.notify.error('dashboard.unableToUpdateNotification'));
    }

    async onScroll(): Promise<void> {
        if (this.hasImprovedHomepageFlag) {
            return;
        }

        const notifications = this.container.nativeElement.querySelectorAll('.notification');
        if (notifications.length) {
            const element = this.container.nativeElement;

            const atBottom = element.scrollTop + element.offsetHeight >= element.scrollHeight;

            if (atBottom && this.notifications.length < this.notificationService.totalNumberOfNotifications) {
                this.goToNextPage();
            }
        }
    }

    onArchiveAll(): void {
        this.isLoading = true;
        this.notificationService
            .archiveAll()
            .then(() => {
                this.reloadCurrentNotifications();
            })
            .catch(() => this.notify.error('dashboard.unableToArchiveAllNotifications'));
    }

    onMarkAllAsRead(): void {
        this.isLoading = true;
        this.notificationService
            .markAllAsRead()
            .then(() => {
                this.reloadCurrentNotifications();
            })
            .catch(() => this.notify.error('dashboard.unableToMarkAllNotificationsAsRead'));
    }

    onClickNotification(n: Notification): void {
        const pendoEvent = n.archivedAt
            ? AnalyticEvents.NotificationsArchivedClicked
            : AnalyticEvents.NotificationsClicked;

        this.analyticService.trackEvent(pendoEvent);

        if (n.hasLink()) {
            if (n.isReadStatus) {
                this.router.navigateByUrl(n.link ?? '/');
                return;
            }
            this.notificationService.toggleRead(n).then(() => this.router.navigateByUrl(n.link ?? '/'));
            return;
        }

        this.activeNotification = n;
        this.isViewNotificationModalOpen = true;
        if (!n.isReadStatus) {
            this.notificationService.toggleRead(n).then(() => this.readStateUpdated.emit());
        }
    }

    async onArchive(n: Notification, isModal?: boolean): Promise<void> {
        const pendoEvent = isModal
            ? AnalyticEvents.NotificationsModalArchived
            : AnalyticEvents.NotificationsMarkAsArchived;

        this.analyticService.trackEvent(pendoEvent);

        this.isViewNotificationModalOpen = false;

        try {
            await n.archive();
            this.notify.success('dashboard.notificationArchived');
            this.reloadCurrentNotifications();
        } catch (error) {
            this.notify.error('dashboard.unableToArchiveNotification');
        }
    }

    async sendToInbox(n: Notification): Promise<void> {
        this.isViewNotificationModalOpen = false;

        this.analyticService.trackEvent(AnalyticEvents.NotificationsMarkAsUnarchived);

        try {
            await n.unarchive();
            this.notify.success('dashboard.notificationSentToInbox');
            this.reloadCurrentNotifications();
        } catch (error) {
            this.notify.error('dashboard.unableToSendNotificationToInbox');
        }
    }

    onClose(): void {
        this.analyticService.trackEvent(AnalyticEvents.NotificationsModalClose);
        this.isViewNotificationModalOpen = false;
        this.notificationService.loadNotifications(this.showArchived);
    }

    async reloadCurrentNotifications(): Promise<void> {
        this.isLoading = true;
        await this.notificationService.refetchNotifications(this.showArchived);
    }

    async goToNextPage(): Promise<void> {
        this.isLoading = true;
        await this.notificationService.incrementPage(this.showArchived);
    }
}
